import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { User } from '../types/auth';
import { authService } from '../services/auth';
import { supabase, getCurrentUser } from '../lib/supabase';
import { logLogin, logLogout, AuditActionTypes } from '../services/auditLogService';
import { createAuditLog } from '../services/supabase/audit-logs';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Function to handle user data updates
  const updateUserData = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('Error updating user data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user data');
      setUser(null);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const currentUser = user?.email || 'Unknown User';
      console.log(`Logout initiated for user: ${currentUser}`);
      
      // Log the logout action with explicit action type
      const logoutDetails = `User logged out from ${window.location.hostname}`;
      
      // Use direct method to ensure the log is created
      await createAuditLog({
        action: 'User Logout',
        user_name: currentUser,
        details: logoutDetails,
        source: 'custom'
      });
      
      console.log(`Logout action logged for user: ${currentUser}`);
      
      // Also use the standard method as a backup
      try {
        await logLogout(currentUser, logoutDetails, queryClient);
      } catch (logError) {
        console.error(`Standard logout logging failed: ${logError}`);
        // Continue even if this fails since we already used the direct method
      }
      
      // Perform the logout
      await authService.logout();
      
      // Clear all local storage and session storage to ensure no cached data remains
      localStorage.clear();
      sessionStorage.clear();
      
      // Finally clear the user state
      setUser(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      console.error('Logout error:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    let authSubscription: { unsubscribe: () => void } | null = null;
    let cachedUser = user;
    let visibilityTimeout: NodeJS.Timeout | null = null;

    // Handle tab/window close
    const handleTabClose = async () => {
      if (user) {
        try {
          await authService.logout();
        } catch (err) {
          console.error('Error during tab close logout:', err);
        }
      }
    };

    async function initializeAuth() {
      try {
        // Only show loading on initial auth
        if (!cachedUser) {
          setIsLoading(true);
        }
        
        // Check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user && mounted) {
          // Use cached user data if available
          if (cachedUser) {
            setUser(cachedUser);
          } else {
            await updateUserData(false);
          }
          cachedUser = user;
        } else if (mounted) {
          setUser(null);
          setError(null);
          cachedUser = null;
        }
        
        // Always ensure loading is false after initialization
        if (mounted) {
          setIsLoading(false);
        }

        // Subscribe to auth changes only after initial check
        if (mounted) {
          const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === 'SIGNED_IN' && session?.user) {
              if (!document.hidden) {
                await updateUserData(false);
                cachedUser = user;
              }
            } else if (event === 'SIGNED_OUT') {
              setUser(null);
              cachedUser = null;
              setError(null);
              setIsLoading(false);
            }
          });
          authSubscription = subscription;
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize auth');
          setUser(null);
          cachedUser = null;
          setIsLoading(false);
        }
      }
    }

    const VISIBILITY_CHECK_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
    let lastVisibleTimestamp = Date.now();

    // Handle visibility changes - verify session less aggressively
    async function handleVisibilityChange() {
      if (document.hidden) {
        lastVisibleTimestamp = Date.now();
      } else if (mounted && user) { // Tab became visible
        const timeHiddenMs = Date.now() - lastVisibleTimestamp;
        console.log(`AuthContext: Tab became visible after ${Math.round(timeHiddenMs / 1000)}s.`);
        if (timeHiddenMs > VISIBILITY_CHECK_THRESHOLD_MS) {
          console.log(`AuthContext: Tab was hidden for > ${VISIBILITY_CHECK_THRESHOLD_MS / 60000} mins. Verifying session.`);
          try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            if (sessionError) {
              console.error('AuthContext: Error verifying session on visibility change:', sessionError.message);
              // Potentially logout or notify user if sessionError indicates invalid session
            } else if (!session?.user) {
              console.log('AuthContext: Session invalid after prolonged tab hidden state. Logging out.');
              await logout();
            } else {
              console.log('AuthContext: Session still valid after prolonged tab hidden state.');
              // Optionally, refresh user data if needed, but onAuthStateChange should handle most cases
              // await updateUserData(false); 
            }
          } catch (err) {
            console.error('AuthContext: Exception during session verification on visibility change:', err);
          }
        } else {
          console.log('AuthContext: Tab hidden for short duration, assuming session is still valid.');
        }
      }
    }

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleTabClose);
    
    // Initialize auth state
    initializeAuth();

    // Cleanup function
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleTabClose);
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log(`Login attempt for user: ${email}`);
      const response = await authService.login({ email, password });
      setUser(response.user);
      
      // Log successful login with explicit action type
      const loginDetails = `Successful login from ${window.location.hostname}`;
      
      // Use direct method to ensure the log is created
      await createAuditLog({
        action: 'User Login',
        user_name: email,
        details: loginDetails,
        source: 'custom'
      });
      
      console.log(`Login action logged for user: ${email}`);
      
      // Also use the standard method as a backup
      try {
        await logLogin(email, loginDetails, queryClient);
      } catch (logError) {
        console.error(`Standard login logging failed: ${logError}`);
        // Continue even if this fails since we already used the direct method
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      console.error('Login error:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData) => {
    setIsLoading(true);
    setError(null);
    try {
      throw new Error('Registration through Supabase Admin UI only');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      isLoading,
      error
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
